import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { BaseDTO } from "@/models/BaseDto";
import { PlatformCertificateService } from "@/services/platform-certificate-service";
import type {
  CertificateAttemptResponse,
  CertificateOrderResponse,
  CertificateVerification,
  CreateCertificateOrderInput,
  FinishCertificateExamResponse,
  PlatformCertificateEntry,
  PlatformCertificateStatus,
  RegisterCertificateBody,
} from "@/models/certificate/PlatformCertificate";
import type { BuyCourseForm } from "@/services/vitrin-service";
import { submitGatewayForm } from "@/features/course/hooks/use-course-purchase";

export interface CertificateIds {
  miniAppUuid: string;
  courseId: string | number;
}

/**
 * One certificate on the course. A course may sell several, each its own
 * journey, so every write and the exam say which one they mean.
 */
export interface CertificateOfferingIds extends CertificateIds {
  offeringId: string | number;
}

/** Carries the HTTP status so callers can tell a 422 refusal from the rest. */
export class CertificateApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

function unwrap<T>(res: BaseDTO<T>, fallbackMessage: string): T {
  if (!res.success || res.status >= 400) {
    // Server messages are Persian and user-facing — surface them verbatim.
    throw new CertificateApiError(res.message || fallbackMessage, res.status);
  }
  return res.data;
}

/** The page of one certificate on a course. */
export const platformCertificateHref = (
  courseId: string | number,
  miniAppUuid: string,
  offeringId: string | number,
) =>
  `/course/${courseId}/certificate/${offeringId}?miniAppUuid=${encodeURIComponent(miniAppUuid)}`;

/** The exam of one certificate — only the completion type has one. */
export const platformCertificateExamHref = (
  courseId: string | number,
  miniAppUuid: string,
  offeringId: string | number,
) =>
  `/course/${courseId}/certificate/${offeringId}/exam?miniAppUuid=${encodeURIComponent(miniAppUuid)}`;

/** The entry for one certificate out of the course's list. */
export const findCertificateEntry = (
  status: PlatformCertificateStatus | null | undefined,
  offeringId: string | number,
): PlatformCertificateEntry | undefined =>
  status?.certificates.find((c) => String(c.offering.id) === String(offeringId));

/** How long a status stays in memory after its last screen unmounts. */
const STATUS_MEMORY_MS = 5 * 60 * 1000;

export const certificateStatusQueryKey = (
  miniAppUuid: string,
  courseId: string | number,
) => ["platform-certificate", miniAppUuid, String(courseId)] as const;

export const certificateAttemptQueryKey = (
  miniAppUuid: string,
  courseId: string | number,
  offeringId: string | number,
) =>
  ["platform-certificate-attempt", miniAppUuid, String(courseId), String(offeringId)] as const;

/**
 * The status endpoint drives every screen — one entry per certificate the
 * course sells. `null` when it sells none (404).
 *
 * It carries a signed download link that expires in 24h, so it is never
 * persisted and always refetched on mount. It does stay in memory for a few
 * minutes, though: a remount (switching the course tabs, coming back from the
 * certificate page) then shows the last known state while the refetch runs,
 * instead of an empty gap. A minutes-old link is still well within its life.
 */
export function usePlatformCertificateStatus({
  miniAppUuid,
  courseId,
  enabled = true,
}: CertificateIds & { enabled?: boolean }) {
  return useQuery<PlatformCertificateStatus | null>({
    queryKey: certificateStatusQueryKey(miniAppUuid, courseId),
    queryFn: async () => {
      const res = await PlatformCertificateService.getStatus(miniAppUuid, courseId);
      if (res.status === 404) return null;
      return unwrap(res, "خطا در دریافت اطلاعات گواهی.");
    },
    enabled: Boolean(miniAppUuid && courseId) && enabled,
    staleTime: 0,
    gcTime: STATUS_MEMORY_MS,
    refetchOnMount: "always",
    meta: { persist: false },
  });
}

/**
 * Shared error handling for every certificate action: show the server's
 * message, then re-fetch the status — a refusal usually means the cached
 * `next_action` is stale (paid in another tab, issued by an admin…).
 */
function useCertificateErrorHandler({ miniAppUuid, courseId }: CertificateIds) {
  const queryClient = useQueryClient();
  return (error: unknown, fallback: string) => {
    toast.error(error instanceof Error ? error.message : fallback);
    void queryClient.invalidateQueries({
      queryKey: certificateStatusQueryKey(miniAppUuid, courseId),
    });
  };
}

export function useRegisterForCertificate(ids: CertificateOfferingIds) {
  const queryClient = useQueryClient();
  const onError = useCertificateErrorHandler(ids);
  return useMutation({
    mutationFn: async (body: RegisterCertificateBody) => {
      const res = await PlatformCertificateService.register(
        ids.miniAppUuid,
        ids.courseId,
        ids.offeringId,
        body,
      );
      unwrap(res, "ثبت‌نام برای گواهی ناموفق بود.");
      return res.message;
    },
    onSuccess: (message) => {
      if (message) toast.success(message);
      return queryClient.invalidateQueries({
        queryKey: certificateStatusQueryKey(ids.miniAppUuid, ids.courseId),
      });
    },
    onError: (e) => onError(e, "ثبت‌نام برای گواهی ناموفق بود."),
  });
}

function parseGatewayForm(form: CertificateOrderResponse["form"]): BuyCourseForm | null {
  if (!form) return null;
  if (typeof form === "object") return form as BuyCourseForm;
  try {
    return JSON.parse(form) as BuyCourseForm;
  } catch {
    return null;
  }
}

/**
 * Creates a retry (kind 1), issuance (kind 2) or re-post (kind 3) order. The
 * caller shows the returned `amount` (tax included) and then calls
 * {@link redirectToGateway}.
 */
export function useCreateCertificateOrder(ids: CertificateOfferingIds) {
  const onError = useCertificateErrorHandler(ids);
  return useMutation({
    mutationFn: async (body: CreateCertificateOrderInput) => {
      const res = await PlatformCertificateService.createOrder(
        ids.miniAppUuid,
        ids.courseId,
        ids.offeringId,
        // This app is the vitrin storefront. Without `source` the backend sends
        // the gateway back to the mini-app frontend, which is a different app
        // on a different domain — the buyer would have no way back.
        { ...body, source: 1 },
      );
      return unwrap(res, "ایجاد سفارش ناموفق بود.");
    },
    onError: (e) => onError(e, "ایجاد سفارش ناموفق بود."),
  });
}

/** Hands the order to the same gateway redirect the course purchase uses. */
export function redirectToGateway(
  order: CertificateOrderResponse,
  { miniAppUuid, courseId }: CertificateIds,
): boolean {
  const form = parseGatewayForm(order.form);
  return form ? submitGatewayForm(form, miniAppUuid, Number(courseId)) : false;
}

/** Starts a sitting (or returns the one in progress) and seeds the attempt cache. */
export function useStartCertificateExam(ids: CertificateOfferingIds) {
  const queryClient = useQueryClient();
  const onError = useCertificateErrorHandler(ids);
  return useMutation<CertificateAttemptResponse>({
    mutationFn: async () => {
      const res = await PlatformCertificateService.startExam(
        ids.miniAppUuid,
        ids.courseId,
        ids.offeringId,
      );
      return unwrap(res, "امکان شروع آزمون وجود ندارد.");
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        certificateAttemptQueryKey(ids.miniAppUuid, ids.courseId, ids.offeringId),
        data,
      );
    },
    onError: (e) => onError(e, "امکان شروع آزمون وجود ندارد."),
  });
}

/**
 * The sitting in progress, `null` when there is none (404). Restores an exam
 * after a reload without ever starting a new sitting.
 */
export function useCurrentCertificateAttempt({
  miniAppUuid,
  courseId,
  offeringId,
}: CertificateOfferingIds) {
  return useQuery<CertificateAttemptResponse | null>({
    queryKey: certificateAttemptQueryKey(miniAppUuid, courseId, offeringId),
    queryFn: async () => {
      const res = await PlatformCertificateService.getCurrentAttempt(
        miniAppUuid,
        courseId,
        offeringId,
      );
      if (res.status === 404) return null;
      return unwrap(res, "خطا در دریافت آزمون.");
    },
    enabled: Boolean(miniAppUuid && courseId && offeringId),
    // Questions are drawn per sitting — never reuse them across attempts, and
    // always re-read remaining_time from the server on mount.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    meta: { persist: false },
  });
}

export function useFinishCertificateExam(ids: CertificateOfferingIds) {
  const queryClient = useQueryClient();
  return useMutation<FinishCertificateExamResponse>({
    mutationFn: async () => {
      const res = await PlatformCertificateService.finishExam(
        ids.miniAppUuid,
        ids.courseId,
        ids.offeringId,
      );
      return unwrap(res, "پایان دادن به آزمون ناموفق بود.");
    },
    // The attempt query is left alone: the exam page is still rendering from
    // it, and its gcTime of 0 drops it once the page unmounts.
    onSettled: () => {
      return queryClient.invalidateQueries({
        queryKey: certificateStatusQueryKey(ids.miniAppUuid, ids.courseId),
      });
    },
  });
}

/**
 * Public verification — no auth. Enabled only for a submitted code, never per
 * keystroke: the endpoint allows 10 calls a minute per IP.
 */
export function useVerifyCertificate(code: string) {
  return useQuery<CertificateVerification>({
    queryKey: ["certificate-verification", code],
    queryFn: async () => {
      const res = await PlatformCertificateService.verify(code);
      return unwrap(res, "استعلام گواهی ناموفق بود.");
    },
    enabled: Boolean(code),
    retry: false,
    meta: { persist: false },
  });
}
